import { Form, Input, Button, message, Divider } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { authApi } from '@tradermind/shared/api';
import { useAuthStore } from '@tradermind/shared/stores';
import { loginSchema, type LoginFormData } from '@tradermind/shared/validators';
import { ROUTES } from '@tradermind/shared/constants';

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const loginMutation = useMutation({
    mutationFn: (data: LoginFormData) => authApi.login(data),
    onSuccess: (response) => {
      const { user, token, refresh_token } = response.data;
      setAuth(user, token, refresh_token);
      message.success(t('login.success'));
      navigate(ROUTES.DASHBOARD);
    },
    onError: (error: { error?: { message?: string } }) => {
      message.error(error.error?.message || t('login.failed'));
    },
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate(data);
  };

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
      <Form.Item
        label={t('login.email')}
        validateStatus={errors.email ? 'error' : ''}
        help={errors.email?.message}
      >
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              prefix={<MailOutlined />}
              placeholder={t('login.email')}
              size="large"
            />
          )}
        />
      </Form.Item>

      <Form.Item
        label={t('login.password')}
        validateStatus={errors.password ? 'error' : ''}
        help={errors.password?.message}
      >
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input.Password
              {...field}
              prefix={<LockOutlined />}
              placeholder={t('login.password')}
              size="large"
            />
          )}
        />
      </Form.Item>

      <div style={{ textAlign: 'right', marginBottom: 16 }}>
        <Link to={ROUTES.AUTH.FORGOT_PASSWORD}>{t('login.forgot_password')}</Link>
      </div>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        block
        loading={loginMutation.isPending}
      >
        {t('login.submit')}
      </Button>

      <Divider plain>{t('common:or', '或')}</Divider>

      <div style={{ textAlign: 'center' }}>
        {t('login.no_account')} <Link to={ROUTES.AUTH.REGISTER}>{t('login.register_link')}</Link>
      </div>
    </Form>
  );
}
